import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronDown,
  Cloud,
  ExternalLink,
  HardDrive,
  Mail,
  MessagesSquare,
  PenLine,
} from 'lucide-react'
import { BrandMark } from '@/components/BrandLogo'

/**
 * The suite's app-switcher glyph: nine filled squares in a 3×3 grid.
 *
 * <p>Drawn here rather than taken from the icon set, because lucide has no
 * nine-dot icon — its nearest is a lined grid, which reads as a table. This is
 * the exact path mail.nevtan.com uses, so the mark is identical across the
 * suite.
 */
function NineDots({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4 8h4V4H4zm6 12h4v-4h-4zm-6 0h4v-4H4zm0-6h4v-4H4zm6 0h4v-4h-4zm6-10v4h4V4zm-6 4h4V4h-4zm6 6h4v-4h-4zm0 6h4v-4h-4z" />
    </svg>
  )
}

/**
 * The suite switcher every NevTan product carries in its masthead.
 *
 * <p>Nine dots, and behind them the whole suite with this product marked as
 * the one you are already on — the same menu mail.nevtan.com opens, so moving
 * between products is the same gesture wherever you start.
 *
 * <p>Every address here was checked as live. NevTan AI itself is listed but
 * not linked: it is the page you are on.
 */
interface Product {
  name: string
  href?: string
  body: string
  icon: ReactNode
}

const PRODUCTS: Product[] = [
  {
    name: 'NevTan AI',
    body: 'Find, install and run open AI models on hardware you already own.',
    icon: <BrandMark alt="" className="h-5 w-5" />,
  },
  {
    name: 'NevTan Mail',
    href: 'https://mail.nevtan.com/',
    body: 'Secure business email, calendar & meetings on your own domain.',
    icon: <Mail size={17} />,
  },
  {
    name: 'NevTan Drive',
    href: 'https://drive.nevtan.com/',
    body: 'Keep, organize & share your files — with storage shared with Mail.',
    icon: <HardDrive size={17} />,
  },
  {
    name: 'NevTan Sign',
    href: 'https://sign.nevtan.com/',
    body: 'Send, sign & manage documents with legally binding e-signatures.',
    icon: <PenLine size={17} />,
  },
  {
    name: 'NevTan Cloud',
    href: 'https://cloud.nevtan.com/',
    body: "Store, sync & share your team's files securely in the cloud.",
    icon: <Cloud size={17} />,
  },
  {
    name: 'NevTan Engage',
    href: 'https://engage.nevtan.com/',
    body: 'Reach & engage your customers across channels from one place.',
    icon: <MessagesSquare size={17} />,
  },
]

export function SuiteSwitcher({
  /** Drops the label, for the application header where width is scarce. */
  compact = false,
  /**
   * Which edge the panel hangs from. A trigger sitting at the right of a bar
   * must open leftwards, or a 340px panel runs straight off the screen.
   */
  align = 'left',
}: {
  compact?: boolean
  align?: 'left' | 'right'
} = {}) {
  const [open, setOpen] = useState(false)
  const [box, setBox] = useState<{ top: number; left: number; width: number } | null>(null)
  const wrap = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  /**
   * Places the panel against the viewport rather than against the trigger.
   *
   * <p>Anchoring to an edge cannot work for both: this trigger sits at the far
   * right of a bar on one screen and mid-nav on another, and at 375px a 340px
   * panel runs off whichever side it is pinned to. Measuring and clamping puts
   * it as close to the trigger as it can be while staying fully on screen.
   */
  const place = () => {
    const rect = trigger.current?.getBoundingClientRect()
    if (!rect) return
    const margin = 12
    const vw = document.documentElement.clientWidth
    const width = Math.min(340, vw - margin * 2)
    const wanted = align === 'right' ? rect.right - width : rect.left
    const left = Math.min(Math.max(margin, wanted), vw - width - margin)
    setBox({ top: rect.bottom + 6, left, width })
  }

  // Kept in place while it is open: scrolling or resizing under an anchored
  // panel would otherwise leave it floating away from its button.
  useEffect(() => {
    if (!open) return
    place()
    const update = () => place()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (!wrap.current?.contains(target) && !panel.current?.contains(target)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrap} className="relative">
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="NevTan products"
        title={compact ? 'NevTan products' : undefined}
        className={`inline-flex items-center gap-1.5 rounded-lg text-[14px] font-bold transition ${
          compact ? 'p-1.5' : 'px-2.5 py-1.5'
        } ${
          open
            ? 'bg-brand-tint/60 text-brand-tint-ink'
            : 'text-brand-body hover:bg-brand-tint/50 hover:text-brand'
        }`}
      >
        <NineDots size={16} />
        {!compact && (
          <>
            <span className="hidden sm:inline">Products</span>
            <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {/*
        Rendered into the body rather than in place. Both mastheads carry a
        `backdrop-filter`, and that makes an element a containing block for
        fixed-position descendants — so a panel positioned in viewport
        coordinates would be offset by the header's own origin and hang off the
        screen. Out here, the coordinates mean what they say.
      */}
      {open &&
        box &&
        createPortal(
          <div
            ref={panel}
            style={{ top: box.top, left: box.left, width: box.width }}
            className="fixed z-50 overflow-hidden rounded-xl border border-brand-line bg-brand-paper shadow-float"
          >
          <div className="border-b border-brand-line px-4 py-3">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-brand">
              The NevTan suite
            </p>
            <p className="mt-0.5 text-[12.5px] text-brand-muted">
              One account across every NevTan product.
            </p>
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-1.5">
            {PRODUCTS.map((product) => {
              const inner = (
                <>
                  <span
                    className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                      product.href ? 'bg-brand-tint text-brand-tint-ink' : 'bg-brand-tint'
                    }`}
                  >
                    {product.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="text-[13.5px] font-bold text-brand-heading">
                        {product.name}
                      </span>
                      {product.href ? (
                        <ExternalLink size={12} className="shrink-0 text-brand-muted" />
                      ) : (
                        <span className="rounded bg-brand-tint px-1.5 py-0.5 text-[10.5px] font-bold text-brand-tint-ink">
                          You&rsquo;re here
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-[12px] leading-snug text-brand-muted">
                      {product.body}
                    </span>
                  </span>
                </>
              )

              // The product you are on is stated, not linked back to itself.
              return product.href ? (
                <a
                  key={product.name}
                  href={product.href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex gap-3 rounded-lg px-3 py-2.5 transition hover:bg-brand-tint/40"
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={product.name}
                  className="flex gap-3 rounded-lg bg-brand-tint/40 px-3 py-2.5"
                >
                  {inner}
                </div>
              )
            })}
          </div>

          <a
            href="https://nevtan.com"
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between border-t border-brand-line px-4 py-3 text-[13.5px] font-bold text-brand transition hover:bg-brand-tint/40"
          >
            Explore the full NevTan suite
            <ExternalLink size={14} />
          </a>
          </div>,
          document.body,
        )}
    </div>
  )
}

/**
 * The same products as rows, for the mobile menu where there is no popover.
 *
 * <p>Carries the icons and descriptions rather than bare names: in a full
 * screen menu there is room for them, and a list of five unfamiliar product
 * names is far harder to scan without them.
 */
export function SuiteLinks() {
  return (
    <>
      {PRODUCTS.filter((p) => p.href).map((product) => (
        <a
          key={product.name}
          href={product.href}
          target="_blank"
          rel="noreferrer"
          className="flex gap-3 rounded-xl px-2 py-2.5 transition active:bg-brand-tint/40"
        >
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand-tint-ink">
            {product.icon}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="text-[14px] font-bold text-brand-heading">{product.name}</span>
              <ExternalLink size={11} className="shrink-0 text-brand-muted" />
            </span>
            <span className="mt-0.5 block text-[12px] leading-snug text-brand-muted">
              {product.body}
            </span>
          </span>
        </a>
      ))}
    </>
  )
}
